import { isFunction } from 'lodash';
import { useRouter } from 'next/router';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import TaskCard from '@/3.0/library/_components/operation/myCreations/_components/TaskCard';
import { useTranslate } from '@/hooks/useTranslate';
import { getFeatureTypes } from '@/utils/router';

import DelayedRender from '../DelayRender';
import { TASK_PANEL_EVENTS } from './events';
import { EmptyIcon } from './icon';
import { getTaskCardRender } from './taskPanelCustomRender';
import { DEFAULT_PAGE_SIZE, useTaskList } from './useTaskList';
import { formatDate } from './utils';

export interface TaskPanelRef {
  refresh: () => void;
}

interface TEmpty {
  emptyText?: React.ReactNode;
  emptyContent?: React.ReactNode;
}

export const Empty: React.FC<TEmpty> = (props) => {
  const { t } = useTranslate();
  return (
    <div className="px-[8px] py-[16px] rounded-[8px] flex justify-center items-center flex-col">
      <EmptyIcon />
      <p className="text-[#11111159] text-[12px]">
        {props.emptyText || t('18432c5cd309e3b1e5dda1b438381f5d')}
      </p>
    </div>
  );
};

interface TTaskPanelProps {
  children?: React.ReactNode | ((data: Record<string, any>) => React.ReactNode);
  emptyContent?: React.ReactNode;
  emptyText?: React.ReactNode;
}

const TaskPanel = forwardRef<TaskPanelRef, TTaskPanelProps>((props, ref) => {
  const { t } = useTranslate();
  const router = useRouter();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { children, emptyText } = props;

  const queryTypes = useMemo(() => getFeatureTypes(router.pathname), [router.pathname]);
  const previousVisiblePagesRef = useRef<Set<number>>(new Set()); // 保存上一次的可见页面（初始为空）

  const {
    taskList,
    loading,
    hasMore,
    loadMore,
    refresh,
    deleteTask,
    deleteMediaItem,
    setVisiblePages,
    updateMediaItemTitle,
  } = useTaskList({
    queryTypes,
    pathname: router.pathname,
  });

  // 使用 ref 保存最新的 refresh 函数，避免事件监听器重复注册
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useImperativeHandle(ref, () => ({
    refresh,
  }));

  useEffect(() => {
    refreshRef.current();
  }, []);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    const handleTaskSubmitted = () => {
      refreshRef.current();
    };

    window.addEventListener(TASK_PANEL_EVENTS.TASK_SUBMITTED, handleTaskSubmitted);

    return () => {
      window.removeEventListener(TASK_PANEL_EVENTS.TASK_SUBMITTED, handleTaskSubmitted);
    };
  }, []);

  // 计算每个 task 所在的页码
  const getTaskPageNum = useCallback((index: number) => {
    return Math.floor(index / DEFAULT_PAGE_SIZE) + 1;
  }, []);

  // IntersectionObserver: 监控哪些 TaskCard 在可见区域
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 直接使用 Set 维护可见页码（从空 Set 开始，重新检测）
    const visiblePagesSet = new Set<number>();

    // 比较两个 Set 是否相同
    const isSetsEqual = (set1: Set<number>, set2: Set<number>) => {
      if (set1.size !== set2.size) return false;
      for (const item of set1) {
        if (!set2.has(item)) return false;
      }
      return true;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        let changed = false;

        // 根据可见性变化更新 Set
        entries.forEach((entry) => {
          const page = Number(entry.target.getAttribute('data-page'));
          if (!isNaN(page)) {
            if (entry.isIntersecting) {
              if (!visiblePagesSet.has(page)) {
                visiblePagesSet.add(page);
                changed = true;
              }
            } else {
              if (visiblePagesSet.has(page)) {
                // 检查该页面是否还有其他可见元素
                const pageElements = container.querySelectorAll(`[data-page="${page}"]`);
                let hasVisible = false;
                pageElements.forEach((el) => {
                  if (el !== entry.target) {
                    const rect = el.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    if (
                      rect.top < containerRect.bottom &&
                      rect.bottom > containerRect.top
                    ) {
                      hasVisible = true;
                    }
                  }
                });

                if (!hasVisible) {
                  visiblePagesSet.delete(page);
                  changed = true;
                }
              }
            }
          }
        });

        // 只在真正变化时才更新
        if (changed && visiblePagesSet.size > 0) {
          const newSet = new Set(visiblePagesSet);
          if (!isSetsEqual(newSet, previousVisiblePagesRef.current)) {
            previousVisiblePagesRef.current = newSet;
            setVisiblePages(newSet);
          }
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 0,
      },
    );

    // 观察所有现有的 TaskCard（包括新渲染的）
    const taskCards = container.querySelectorAll('[data-page]');
    taskCards.forEach((card) => observer.observe(card));

    // 立即检查初始可见的元素
    const containerRect = container.getBoundingClientRect();
    taskCards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      if (rect.top < containerRect.bottom && rect.bottom > containerRect.top) {
        const page = Number(card.getAttribute('data-page'));
        if (!isNaN(page)) {
          visiblePagesSet.add(page);
        }
      }
    });

    // 初始更新
    if (visiblePagesSet.size > 0) {
      const newSet = new Set(visiblePagesSet);
      if (!isSetsEqual(newSet, previousVisiblePagesRef.current)) {
        previousVisiblePagesRef.current = newSet;
        setVisiblePages(newSet);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [taskList.length, setVisiblePages]); // 恢复 taskList.length 依赖

  const defaultTaskContent = (
    <div className="flex flex-col gap-[8px]">
      <div className="flex justify-between leading-[30px]">
        <span>{t('cfea33cd5cc402705358d2def1e7c902')}</span>
      </div>
      <div
        ref={scrollContainerRef}
        className="flex-1 scroll-overlay"
        style={{ maxHeight: 'calc(100vh - 150px)' }}
      >
        <div className="flex flex-col gap-[12px]">
          {/* 空状态：只在没有任务且不在加载时显示 */}
          {taskList.length === 0 && !loading && <Empty emptyText={emptyText} />}
          {/* 任务列表 */}
          {taskList.map((task, index) => {
            const {
              header: renderHeader,
              mediaLayout: renderMediaLayout,
              moreMenu: renderMoreMenu,
            } = getTaskCardRender(task.cloudTaskType);

            const pageNum = getTaskPageNum(index);

            return (
              <TaskCard
                key={`batch-${task.id}`}
                {...task}
                data-page={pageNum}
                createdAt={formatDate(task.createTime)}
                onDelete={(mediaItemId) => {
                  deleteMediaItem(task.id, mediaItemId);
                }}
                onDeleteBatch={() => {
                  deleteTask(task.id);
                }}
                onRename={(mediaItemId, title) => {
                  updateMediaItemTitle(task.id, mediaItemId, title);
                }}
                renderHeader={renderHeader}
                renderMediaLayout={renderMediaLayout}
                renderMoreMenu={renderMoreMenu}
              />
            );
          })}
          {/* Loading 指示器：始终在底部显示 */}
          {loading && (
            <DelayedRender>
              <div className="text-center py-4 text-[#999]">{t('merror.edit.add')}</div>
            </DelayedRender>
          )}
          {/* Observer target for infinite scroll */}
          {hasMore && !loading && <div ref={loadMoreRef} className="h-[1px]" />}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full px-[12px] py-[10px]">
      {children
        ? isFunction(children)
          ? !loading
            ? children({ taskList, defaultTaskContent })
            : defaultTaskContent
          : children
        : defaultTaskContent}
    </div>
  );
});

TaskPanel.displayName = 'TaskPanel';

export default memo(TaskPanel);
